import {
  buildArchetypeCrosstab,
  fetchAggregates,
  formatAxisMean,
  formatCount,
  formatCrossPartyPct,
  formatDateRange,
  formatN,
  formatPublishedAt,
  isSuppressed,
  PARTY_LABELS,
  type AggregatesResponseBody,
  type Cell,
  type PartyBucketKey,
} from "./aggregates.ts";

const app = document.getElementById("compare-app");

function el<K extends keyof HTMLElementTagNameMap>(tag: K, props: Record<string, unknown> = {}, children: (Node | string)[] = []): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  Object.assign(node, props);
  for (const child of children) node.append(child);
  return node;
}

/** A table cell for a figure the server may have withheld (fewer than
 * `min_cell_n` respondents) — those render as muted "too few responses". */
function countCell(value: Cell<number>, className = ""): HTMLTableCellElement {
  const suppressed = isSuppressed(value);
  const classes = [className, suppressed ? "compare__suppressed" : ""].filter(Boolean).join(" ");
  return el("td", { className: classes, textContent: formatCount(value) });
}

/** docs/ARCHITECTURE.md §5.3 [LOCKED]: the primary presentation, always a
 * party crosstab with n shown per cell — never a single headline number. */
function renderCrosstab(data: AggregatesResponseBody): HTMLElement {
  const rows = buildArchetypeCrosstab(data);
  const table = el("table", { className: "compare__crosstab" });

  const thead = el("thead");
  const headRow = el("tr");
  headRow.append(el("th", { textContent: "Archetype" }));
  headRow.append(el("th", { textContent: "Responses" }));
  for (const party of data.parties) {
    headRow.append(el("th", { textContent: PARTY_LABELS[party as PartyBucketKey] }));
  }
  thead.append(headRow);
  table.append(thead);

  const tbody = el("tbody");
  for (const row of rows) {
    const tr = el("tr");
    tr.append(el("td", { textContent: row.label }));
    tr.append(countCell(row.total, "compare__n"));
    for (const party of data.parties) {
      tr.append(countCell(row.byParty[party as PartyBucketKey]));
    }
    tbody.append(tr);
  }
  table.append(tbody);

  return table;
}

function renderAxisSummary(data: AggregatesResponseBody): HTMLElement {
  const section = el("div", { className: "compare__axes" });
  section.append(el("h3", { textContent: "Average position by declared party" }));

  const table = el("table", { className: "compare__axis-table" });
  const thead = el("thead");
  const headRow = el("tr");
  headRow.append(el("th", { textContent: "Axis" }));
  for (const party of data.parties) {
    headRow.append(el("th", { textContent: PARTY_LABELS[party as PartyBucketKey] }));
  }
  thead.append(headRow);
  table.append(thead);

  const tbody = el("tbody");
  const axisRow = (label: string, values: Record<PartyBucketKey, Cell<number>>) => {
    const tr = el("tr");
    tr.append(el("td", { textContent: label }));
    for (const party of data.parties) {
      const value = values[party as PartyBucketKey];
      tr.append(el("td", { className: isSuppressed(value) ? "compare__suppressed" : "", textContent: formatAxisMean(value) }));
    }
    return tr;
  };
  tbody.append(axisRow("Federal scope (limits ↔ expands)", data.by_axis.axis_a));
  tbody.append(axisRow("Institutional process (works within the rules ↔ bypasses them)", data.by_axis.axis_b));
  table.append(tbody);
  section.append(table);
  return section;
}

function renderCrossPartyAgreement(data: AggregatesResponseBody): HTMLElement {
  const section = el("div", { className: "compare__cross-party" });
  section.append(el("h3", { textContent: "Average cross-party support" }));
  const list = el("ul");
  list.append(el("li", { textContent: `Declared Democrats: ${formatCrossPartyPct(data.cross_party_agreement.D)}` }));
  list.append(el("li", { textContent: `Declared Republicans: ${formatCrossPartyPct(data.cross_party_agreement.R)}` }));
  section.append(list);
  return section;
}

/** docs/ARCHITECTURE.md §8.4 [LOCKED]: sampling honesty — the limitation
 * statement goes above the results, never a footnote, on every page that
 * shows aggregate data. */
function renderSamplingNotice(data: AggregatesResponseBody): HTMLElement {
  const notice = el("p", {
    className: "compare__notice",
    textContent:
      "This is not a scientific poll. The data comes from a self-selected online sample and does not represent the population as a whole. See the ",
  });
  notice.append(el("a", { href: "/methodology/", textContent: "methodology page" }));
  notice.append(document.createTextNode("."));
  return notice;
}

/** §8.4: n, date range, and the quality-filter threshold, published
 * alongside every figure — not just once on the methodology page. */
function renderFigureContext(data: AggregatesResponseBody): HTMLElement {
  return el("p", {
    className: "compare__context",
    textContent:
      `Responses: ${formatN(data.n)} · ${formatDateRange(data.date_range)} · ` +
      `answers with a quality score below ${data.quality_threshold.toFixed(1)} excluded · ` +
      `groups with fewer than ${data.min_cell_n ?? 10} responses hidden · ` +
      `updated at most once a day, figures as of ${formatPublishedAt(data.published_at)}`,
  });
}

async function init(): Promise<void> {
  if (!app) return;
  try {
    const data = await fetchAggregates();
    app.replaceChildren();
    app.append(renderSamplingNotice(data));
    app.append(renderFigureContext(data));
    if (typeof data.published_at !== "number") {
      app.append(
        el("p", {
          textContent:
            "No results have been published yet. Figures appear once enough responses are in, and are updated at most daily. Check back soon.",
        }),
      );
      return;
    }
    app.append(renderCrosstab(data));
    app.append(renderAxisSummary(data));
    app.append(renderCrossPartyAgreement(data));
  } catch (err) {
    app.replaceChildren(
      el("p", { className: "screen screen--error", textContent: err instanceof Error ? err.message : "Failed to load results." }),
    );
  }
}

void init();
